import { useState, useEffect } from "react";
import { useApi } from "../../hooks/use-api";
import { useNotify } from "../../hooks/use-notify";
import { useSingleton } from "../../hooks/use-singleton";
import {
  apiGetCompatitiveExam,
  apiGetPlans,
  API_REQUEST_ACCESS,
  API_SUBMIT_PAYMENT_PROOF,
  apiCancelPayment,
  apiPaymentSummary,
  API_GET_MEDIUM_OF_INSTRUCTION,
  API_FOCUS,
} from "../../utils/api-routes";
import { COMPETITIVE_EXAM_STATIC_DATA } from "../../utils/constant";

const SCHOOL_BASED_EXAMS = ["TN_STATE_BOARD", "ICSE", "CAIE", "CBSE"];
const ASPIRANT_COURSES = ["TNPSC", "UPSC", "NEET", "JEE", "Banking"];

const useHandler = () => {
  const { post, get, del } = useApi();
  const { mail, role } = useSingleton();
  const [selectedExams, setSelectedExams] = useState([]);
  const { toggleNotify } = useNotify();
  const [competitiveExamsList, setCompetitiveExamsList] = useState();
  const [plansList, setPlansList] = useState([]);
  const [accessRequestDetails, setAccessRequestDetails] = useState(null);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [getMediums, setGetMediums] = useState([]);

  const requestAccess = async (payload) => {
    setLoading(true);
    try {
      const res = await post(API_REQUEST_ACCESS, payload);
      if (res) {
        // Extract data directly from Axios response
        const responseData = res.data;
        console.log("ACCESS REQUEST SUCCESS DATA:", responseData);
        setAccessRequestDetails(responseData);
        toggleNotify({
          type: "success",
          title: "Request Submitted",
          description: "Your access request has been initiated successfully.",
        });
        return responseData;
      }
    } catch (e) {
      console.log("ERROR REQUESTING ACCESS", e);
      const errorMessage =
        e?.response?.data?.detail ||
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        "Something went wrong while submitting your request.";
      toggleNotify({
        type: "error",
        title: "Request Failed",
        description: errorMessage,
      });
      return null;
    } finally {
      setLoading(false);
    }
  };
  const addToPreferences = async (item) => {
    setLoading(true);

    const payload = {
      email: mail,
      focus: [item.title],
    };

    try {
      const res = await post(API_FOCUS, payload);

      if (res) {
        const responseData = res.data;

        // Update preferred exams in state
        setCompetitiveExamsList((prev) => {
          if (!prev) return prev;

          return {
            ...prev,
            preferred: prev.all.filter((exam) =>
              responseData.focus.includes(exam.exam),
            ),
            all: prev.all.map((exam) => ({
              ...exam,
              is_preferred: responseData.focus.includes(exam.exam),
            })),
          };
        });

        toggleNotify({
          type: "success",
          title: "Preferences added",
          description: "Preferences added successfully",
        });

        return responseData;
      }
    } catch (e) {
      console.log("ERROR REQUESTING ACCESS", e);

      toggleNotify({
        type: "error",
        title: "Request Failed",
        description:
          e?.response?.data?.detail ||
          e?.response?.data?.message ||
          e?.response?.data?.error ||
          "Something went wrong while submitting your request.",
      });

      return null;
    } finally {
      setLoading(false);
    }
  };

  const submitPaymentProof = async (payload) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("request_uuid", payload.request_uuid);
      formData.append("utr_id", payload.utr_id);
      formData.append("screenshot", payload.screenshot);

      const res = await post(API_SUBMIT_PAYMENT_PROOF, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (res) {
        toggleNotify({
          type: "success",
          title: "Proof Submitted",
          description: "Your payment verification is in progress.",
        });
        return true;
      }
    } catch (e) {
      console.log("ERROR SUBMITTING PROOF", e);
      const errorMessage =
        e?.response?.data?.detail ||
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        "Could not submit payment proof. Please try again.";
      toggleNotify({
        type: "error",
        title: "Submission Failed",
        description: errorMessage,
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const cancelPayment = async (orderId) => {
    setLoading(true);
    try {
      const res = await del(apiCancelPayment(orderId));
      if (res) {
        toggleNotify({
          type: "success",
          title: "Payment Cancelled",
          description: "The payment process has been successfully cancelled.",
        });
        return true;
      }
    } catch (e) {
      console.log("ERROR CANCELLING PAYMENT", e);
      toggleNotify({
        type: "error",
        title: "Cancellation Failed",
        description: "Could not cancel the payment. Please try again.",
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const getPaymentSummary = async (orderId) => {
    setLoading(true);
    try {
      const res = await get(apiPaymentSummary(orderId));
      if (res) {
        setPaymentSummary(res.data);
        return res.data;
      }
    } catch (e) {
      console.log("ERROR FETCHING PAYMENT SUMMARY", e);
    } finally {
      setLoading(false);
    }
  };

  const getPlans = async (courses = []) => {
    setLoading(true);

    try {
      console.log("COURSES SENT TO PLANS API:", courses);

      const url = apiGetPlans(courses);

      console.log("PLANS API URL:", url);

      const res = await get(url);

      console.log("PLANS API RES:", res);

      if (res) {
        const data = Array.isArray(res)
          ? res
          : res?.data?.data || res?.data || [];

        console.log("SETTING PLANS LIST:", data);

        setPlansList(data);

        return data;
      }

      return [];
    } catch (e) {
      console.log("ERROR FETCHING PLANS:", e);
      console.log("ERROR RESPONSE:", e?.response);
      console.log("ERROR DATA:", e?.response?.data);

      const errorMessage =
        e?.response?.data?.detail ||
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        "Could not fetch plans. Please try again.";

      toggleNotify({
        type: "error",
        title: "Fetch Failed",
        description: errorMessage,
      });

      return null;
    } finally {
      setLoading(false);
    }
  };
  const getCompetitiveExamDetails = async () => {
    try {
      const res = await get(apiGetCompatitiveExam(mail));
      if (res) {
        setCompetitiveExamsList(res?.data || {});
      }
    } catch (e) {
      console.log("ERROR COMES FROM COMPATITIVE EXAM API ", e);
    }
  };

  useEffect(() => {
    getCompetitiveExamDetails();
  }, []);

  useEffect(() => {
    if (competitiveExamsList?.all?.length) {
      const preferredExams = competitiveExamsList.all
        .filter((item) => item.is_preferred)
        .map((item) => item.exam);

      setSelectedExams(preferredExams);
    }
  }, [competitiveExamsList]);

  const getExamCards = (examList = []) => {
    return COMPETITIVE_EXAM_STATIC_DATA.filter((card) =>
      examList.some(
        (item) => item.exam?.toUpperCase() === card.title?.toUpperCase(),
      ),
    );
  };

  const isExamPreferred = (examTitle) => {
    return (
      competitiveExamsList?.all?.find(
        (exam) => exam.exam?.toUpperCase() === examTitle?.toUpperCase(),
      )?.is_preferred || false
    );
  };

  const preferredExams = getExamCards(competitiveExamsList?.preferred);

  const allExams = getExamCards(
    competitiveExamsList?.all?.filter((exam) => !exam.is_preferred),
  );

  const getMediumOfInstructionsInCompatitiveExam = async (selectedExams) => {
    console.log("Entered function", selectedExams);

    try {
      if (!selectedExams?.length) {
        setGetMediums([]);
        return;
      }

      const exams = Array.isArray(selectedExams)
        ? selectedExams
        : [selectedExams];

      const responses = await Promise.all(
        exams.map(async (exam) => {
          const focus = typeof exam === "string" ? exam : exam?.title;

          if (!focus) return [];

          const res = await get(
            `${API_GET_MEDIUM_OF_INSTRUCTION}?focus=${encodeURIComponent(focus)}`,
          );

          return res?.data?.medium_of_instruction || [];
        }),
      );

      // Combine all mediums and remove duplicates
      const uniqueMediums = [...new Set(responses.flat())];

      setGetMediums(uniqueMediums);

      return uniqueMediums;
    } catch (e) {
      console.log("ERROR COMES FROM API MEDIUM OF INSTRUCTION", e);
      setGetMediums([]);
      return [];
    }
  };

  return {
    preferredExams,
    allExams,
    competitiveExamsList,
    getMediums,
    setGetMediums,
    getMediumOfInstructionsInCompatitiveExam,
    requestAccess,
    getCompetitiveExamDetails,
    getPlans,
    plansList,
    accessRequestDetails,
    submitPaymentProof,
    cancelPayment,
    getPaymentSummary,
    paymentSummary,
    mail,
    role,
    loading,
    SCHOOL_BASED_EXAMS,
    ASPIRANT_COURSES,
    selectedExams,
    setSelectedExams,
    addToPreferences,
    isExamPreferred,
  };
};

export default useHandler;
